import { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft, Check, User, Building2, Target } from 'lucide-react';
import axios from 'axios';

// Nettoyer l'URL pour éviter les double slashes
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const API = `${BACKEND_URL}/api`;

// Créer une instance Axios avec timeout et configuration optimisée
const axiosInstance = axios.create({
  baseURL: API,
  timeout: 8000, // 8 secondes de timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

const steps = [
  { id: 0, label: 'Contact', icon: User },
  { id: 1, label: 'Entreprise', icon: Building2 },
  { id: 2, label: 'Objectifs', icon: Target },
];

// Libellés lisibles pour la source de découverte (récapitulatif + envoi).
const SOURCE_LABELS = {
  ia: 'Une IA (ChatGPT, Gemini, Perplexity…)',
  google: 'Recherche Google',
  reseaux: 'Réseaux sociaux',
  recommandation: 'Recommandation / bouche-à-oreille',
  autre: 'Autre',
};

const slideVariants = {
  enter: (direction) => ({ x: direction > 0 ? 100 : -100, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (direction) => ({ x: direction < 0 ? 100 : -100, opacity: 0 }),
};

export default function AuditForm() {
  const sectionRef = useScrollReveal();
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const abortControllerRef = useRef(null);

  const [form, setForm] = useState({
    nom: '',
    email: '',
    telephone: '',
    type_entreprise: '',
    type_entreprise_autre: '',
    objectifs: '',
    source_decouverte: '',
    source_decouverte_autre: '',
  });

  const [errors, setErrors] = useState({});

  const updateField = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  }, []);

  const validateStep = useCallback(() => {
    const newErrors = {};

    if (step === 0) {
      if (!form.nom.trim()) newErrors.nom = 'Veuillez entrer votre nom';
      if (!form.email.trim()) newErrors.email = 'Veuillez entrer votre email';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'Email invalide';
      if (!form.telephone.trim()) newErrors.telephone = 'Veuillez entrer votre téléphone';
    }

    if (step === 1) {
      if (!form.type_entreprise) newErrors.type_entreprise = 'Veuillez sélectionner un type';
    }

    if (step === 2) {
      if (!form.objectifs.trim()) newErrors.objectifs = 'Veuillez décrire vos objectifs';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [step, form]);

  const nextStep = useCallback(() => {
    if (!validateStep()) return;
    setDirection(1);
    setStep(prev => Math.min(prev + 1, 2));
  }, [validateStep]);

  const prevStep = useCallback(() => {
    setDirection(-1);
    setStep(prev => Math.max(prev - 1, 0));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!validateStep()) return;
    setLoading(true);
    
    // Annuler les requêtes précédentes
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    try {
      // Résoudre la source de découverte en une valeur lisible ('autre' → texte saisi).
      const sourceResolue =
        form.source_decouverte === 'autre'
          ? (form.source_decouverte_autre.trim() || 'Autre')
          : (SOURCE_LABELS[form.source_decouverte] || '');
      const { source_decouverte_autre, ...rest } = form;
      const payload = { ...rest, source_decouverte: sourceResolue };

      await axiosInstance.post('/audit', payload, {
        signal: abortControllerRef.current.signal,
      });
      setSubmitted(true);
      toast.success('Demande envoyée avec succès !', {
        description: 'Notre équipe vous contactera sous 24h.',
      });
    } catch (err) {
      if (err.name === 'AbortError') return; // Requête annulée, ignorer
      
      const errorMsg = err.response?.data?.detail || 'Erreur lors de l\'envoi';
      toast.error('Erreur lors de l\'envoi', {
        description: errorMsg || 'Veuillez réessayer plus tard.',
      });
    } finally {
      setLoading(false);
    }
  }, [form, validateStep]);

  if (submitted) {
    return (
      <section id="audit" data-testid="audit-section" className="py-24 md:py-32">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="max-w-lg mx-auto text-center p-12 rounded-3xl border border-border/50 bg-card"
          >
            <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-green-500" />
            </div>
            <h3 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground mb-4">
              Merci pour votre demande !
            </h3>
            <p className="text-muted-foreground leading-relaxed">
              Notre équipe d'experts IA analysera vos besoins et vous contactera
              sous 24 heures pour planifier votre audit gratuit.
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="audit" data-testid="audit-section" className="py-24 md:py-32">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div ref={sectionRef} className="scroll-reveal text-center mb-16">
          <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[hsl(211,100%,50%)] dark:text-[hsl(211,100%,55%)] mb-4">
            Commencez maintenant
          </p>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-foreground mb-6">
            Réservez votre audit gratuit
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Remplissez ce formulaire et notre équipe vous proposera un plan
            d'automatisation personnalisé pour votre entreprise.
          </p>
        </div>

        <div className="max-w-xl mx-auto">
          {/* Step Indicator */}
          <div className="flex items-center justify-center gap-2 mb-10" data-testid="step-indicator">
            {steps.map((s, i) => {
              const StepIcon = s.icon;
              const isActive = step === i;
              const isDone = step > i;
              return (
                <div key={s.id} className="flex items-center">
                  <div className="flex items-center gap-2">
                    <div
                      className={`step-indicator w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                        isDone
                          ? 'bg-[hsl(211,100%,50%)] text-white'
                          : isActive
                            ? 'bg-[hsl(211,100%,50%)]/15 text-[hsl(211,100%,50%)] dark:text-[hsl(211,100%,55%)] border-2 border-[hsl(211,100%,50%)]'
                            : 'bg-secondary text-muted-foreground'
                      }`}
                    >
                      {isDone ? <Check className="w-4 h-4" /> : <StepIcon className="w-4 h-4" />}
                    </div>
                    <span className={`hidden sm:inline text-sm font-medium ${
                      isActive ? 'text-foreground' : 'text-muted-foreground'
                    }`}>
                      {s.label}
                    </span>
                  </div>
                  {i < steps.length - 1 && (
                    <div className={`w-12 sm:w-20 h-[2px] mx-3 rounded-full transition-colors duration-300 ${
                      step > i ? 'bg-[hsl(211,100%,50%)]' : 'bg-border'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Form Card */}
          <div className="rounded-3xl border border-border/50 bg-card p-8 md:p-10">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={step}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              >
                {step === 0 && (
                  <div className="space-y-6">
                    <div>
                      <Label htmlFor="nom" className="text-sm font-medium mb-2 block">Nom complet</Label>
                      <Input
                        id="nom"
                        data-testid="input-nom"
                        value={form.nom}
                        onChange={(e) => updateField('nom', e.target.value)}
                        placeholder="Jean Dupont"
                        className={`h-12 rounded-xl bg-background ${errors.nom ? 'border-destructive' : ''}`}
                      />
                      {errors.nom && <p className="text-xs text-destructive mt-1">{errors.nom}</p>}
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-sm font-medium mb-2 block">Adresse email</Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="input-email"
                        value={form.email}
                        onChange={(e) => updateField('email', e.target.value)}
                        placeholder="jean@entreprise.tn"
                        className={`h-12 rounded-xl bg-background ${errors.email ? 'border-destructive' : ''}`}
                      />
                      {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
                    </div>
                    <div>
                      <Label htmlFor="telephone" className="text-sm font-medium mb-2 block">Téléphone</Label>
                      <Input
                        id="telephone"
                        type="tel"
                        data-testid="input-telephone"
                        value={form.telephone}
                        onChange={(e) => updateField('telephone', e.target.value)}
                        placeholder="+216 29 275 042"
                        className={`h-12 rounded-xl bg-background ${errors.telephone ? 'border-destructive' : ''}`}
                      />
                      {errors.telephone && <p className="text-xs text-destructive mt-1">{errors.telephone}</p>}
                    </div>
                  </div>
                )}

                {step === 1 && (
                  <div className="space-y-6">
                    <div>
                      <Label className="text-sm font-medium mb-2 block">Type d'entreprise</Label>
                      <Select value={form.type_entreprise} onValueChange={(v) => updateField('type_entreprise', v)}>
                        <SelectTrigger data-testid="select-type-entreprise" className={`h-12 rounded-xl bg-background ${errors.type_entreprise ? 'border-destructive' : ''}`}>
                          <SelectValue placeholder="Sélectionnez votre secteur" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="restaurant">Restaurant / Restauration</SelectItem>
                          <SelectItem value="medical">Cabinet médical / Santé</SelectItem>
                          <SelectItem value="retail">Commerce / Retail</SelectItem>
                          <SelectItem value="service">Services professionnels</SelectItem>
                          <SelectItem value="autre">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                      {errors.type_entreprise && <p className="text-xs text-destructive mt-1">{errors.type_entreprise}</p>}
                    </div>
                    {form.type_entreprise === 'autre' && (
                      <div className="mt-4">
                        <Label htmlFor="type_entreprise_autre" className="text-sm font-medium mb-2 block">Précisez votre type d'entreprise</Label>
                        <Textarea
                          id="type_entreprise_autre"
                          data-testid="textarea-type-entreprise-autre"
                          value={form.type_entreprise_autre}
                          onChange={(e) => updateField('type_entreprise_autre', e.target.value)}
                          placeholder="Décrivez votre secteur d'activité..."
                          className="flex w-full border border-input px-3 py-2 text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm min-h-[140px] rounded-xl bg-background resize-none"
                        />
                      </div>
                    )}
                    <div className="p-6 rounded-2xl bg-secondary/50 border border-border/30">
                      <h4 className="text-sm font-semibold text-foreground mb-2">Ce que comprend l'audit</h4>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-[hsl(211,100%,50%)]" />
                          Analyse de vos processus actuels
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-[hsl(211,100%,50%)]" />
                          Identification des opportunités d'automatisation
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-[hsl(211,100%,50%)]" />
                          Plan d'action personnalisé
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-[hsl(211,100%,50%)]" />
                          Estimation du ROI attendu
                        </li>
                      </ul>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-6">
                    <div>
                      <Label htmlFor="objectifs" className="text-sm font-medium mb-2 block">
                        Décrivez vos objectifs
                      </Label>
                      <Textarea
                        id="objectifs"
                        data-testid="textarea-objectifs"
                        value={form.objectifs}
                        onChange={(e) => updateField('objectifs', e.target.value)}
                        placeholder="Quels processus souhaitez-vous automatiser ? Quels sont vos principaux défis ?"
                        className={`min-h-[140px] rounded-xl bg-background resize-none ${errors.objectifs ? 'border-destructive' : ''}`}
                      />
                      {errors.objectifs && <p className="text-xs text-destructive mt-1">{errors.objectifs}</p>}
                    </div>
                    <div>
                      <Label className="text-sm font-medium mb-2 block">
                        Comment nous avez-vous découverts ? <span className="text-muted-foreground font-normal">(facultatif)</span>
                      </Label>
                      <Select value={form.source_decouverte} onValueChange={(v) => updateField('source_decouverte', v)}>
                        <SelectTrigger data-testid="select-source-decouverte" className="h-12 rounded-xl bg-background">
                          <SelectValue placeholder="Sélectionnez une option" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ia">Une IA (ChatGPT, Gemini, Perplexity…)</SelectItem>
                          <SelectItem value="google">Recherche Google</SelectItem>
                          <SelectItem value="reseaux">Réseaux sociaux (Instagram, LinkedIn…)</SelectItem>
                          <SelectItem value="recommandation">Recommandation / bouche-à-oreille</SelectItem>
                          <SelectItem value="autre">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                      {form.source_decouverte === 'autre' && (
                        <div className="mt-4">
                          <Input
                            id="source_decouverte_autre"
                            data-testid="input-source-decouverte-autre"
                            value={form.source_decouverte_autre}
                            onChange={(e) => updateField('source_decouverte_autre', e.target.value)}
                            placeholder="Précisez comment vous nous avez trouvés…"
                            className="h-12 rounded-xl bg-background"
                          />
                        </div>
                      )}
                    </div>
                    <div className="p-6 rounded-2xl bg-secondary/50 border border-border/30">
                      <h4 className="text-sm font-semibold text-foreground mb-3">Récapitulatif</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Nom</span>
                          <span className="font-medium text-foreground">{form.nom || '—'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Email</span>
                          <span className="font-medium text-foreground">{form.email || '—'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Secteur</span>
                          <span className="font-medium text-foreground capitalize">{form.type_entreprise || '—'}</span>
                        </div>
                        {form.source_decouverte && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Découvert via</span>
                            <span className="font-medium text-foreground">
                              {SOURCE_LABELS[form.source_decouverte] || '—'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Navigation buttons */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-border/50">
              {step > 0 ? (
                <Button
                  variant="ghost"
                  onClick={prevStep}
                  data-testid="form-prev-button"
                  className="rounded-full px-6"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Retour
                </Button>
              ) : (
                <div />
              )}

              {step < 2 ? (
                <Button
                  onClick={nextStep}
                  data-testid="form-next-button"
                  className="rounded-full px-8 h-11 font-semibold bg-[hsl(211,100%,50%)] hover:bg-[hsl(211,100%,45%)] text-white btn-premium no-color-transition"
                >
                  Suivant
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  data-testid="form-submit-button"
                  className="rounded-full px-8 h-11 font-semibold bg-[hsl(211,100%,50%)] hover:bg-[hsl(211,100%,45%)] text-white btn-premium no-color-transition"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Envoi...
                    </span>
                  ) : (
                    <>
                      Envoyer ma demande
                      <Check className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
